'use client';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSchoolProfile } from '@hooks/useSchoolProfile';

import { showToast } from '@components/ToastContainer';

import { extractContactDataFromProfile } from '@utils/helpers/contactValidation';
import {
  FORM_STEPS,
  type FormAction,
  type FormErrors,
  type FormStep,
  INITIAL_FORM_DATA,
  type ShiftFormData,
  shiftValidationSchema,
} from '@utils/shiftConstants';

// Form reducer for state management
const formReducer = (state: ShiftFormData, action: FormAction): ShiftFormData => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESET_FORM':
      return { ...INITIAL_FORM_DATA };
    default:
      return state;
  }
};

// Custom hook for error handling
export const useShiftErrors = () => {
  const [errors, setErrors] = useState<FormErrors>({});

  const setFieldError = useCallback((field: string, error: string) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  return { errors, setFieldError, clearFieldError, clearAllErrors };
};

// Custom hook for form validation
export const useFormValidation = (data: ShiftFormData, step: FormStep) => {
  return useMemo(() => {
    const errorFields: string[] = [];

    if (step === 'details') {
      const schema = shiftValidationSchema.details;

      // Check required fields
      const missingFields = schema.required.filter((field) => !data[field as keyof ShiftFormData]);

      if (missingFields.length > 0) {
        errorFields.push(...missingFields);
      }

      // Validate shift date
      if (data.shift_date && !schema.validators.shift_date(data.shift_date)) {
        errorFields.push('shift_date');
      }

      // Validate time fields with form data context
      if (data.time_from && !schema.validators.time_from(data.time_from, data)) {
        errorFields.push('time_from');
      }

      if (data.time_till && !schema.validators.time_till(data.time_till, data)) {
        errorFields.push('time_till');
      }
    } else if (step === 'contacts') {
      const schema = shiftValidationSchema.contacts;

      // Validate contact phone if provided
      if (data.contact_phone && !schema.validators.contact_phone(data.contact_phone)) {
        errorFields.push('contact_phone');
      }
    }

    return {
      isValid: errorFields.length === 0,
      errorFields,
    };
  }, [data, step]);
};

// Main custom hook for form management
export const useCreateShiftForm = () => {
  const { t } = useTranslation('common');
  const { schoolProfile } = useSchoolProfile();
  const [currentStep, setCurrentStep] = useState<FormStep>(FORM_STEPS.DETAILS);
  const [formData, dispatch] = useReducer(formReducer, { ...INITIAL_FORM_DATA });
  const { errors, setFieldError, clearFieldError, clearAllErrors } = useShiftErrors();
  const { isValid: isStepValid, errorFields } = useFormValidation(formData, currentStep);

  // Pre-populate contact fields with school's existing data
  useEffect(() => {
    if (schoolProfile) {
      const contactData = extractContactDataFromProfile(schoolProfile);

      // Update form data with school's contact information
      if (contactData.email && !formData.contact_email) {
        dispatch({ type: 'UPDATE_FIELD', field: 'contact_email', value: contactData.email });
      }
      if (contactData.phone && !formData.contact_phone) {
        dispatch({ type: 'UPDATE_FIELD', field: 'contact_phone', value: contactData.phone });
      }
    }
  }, [schoolProfile, formData.contact_email, formData.contact_phone]);

  const updateField = useCallback(
    (field: keyof ShiftFormData, value: string | boolean) => {
      dispatch({ type: 'UPDATE_FIELD', field, value });
      // Clear field error when user starts typing
      if (errors[field]) {
        clearFieldError(field);
      }
    },
    [errors, clearFieldError],
  );

  const getValidationToastMessage = useCallback(
    (fields: string[], data: ShiftFormData) => {
      if (fields.includes('shift_date') && data.shift_date) {
        return {
          title: t('toast.errorGeneric'),
          message: t('toast.shiftInPast'),
        };
      }
      if (fields.includes('time_from') || fields.includes('time_till')) {
        return {
          title: t('toast.errorTime'),
          message: t('toast.errorTimeDescription'),
        };
      }
      if (fields.includes('contact_phone') && data.contact_phone) {
        return {
          title: t('toast.errorPhone'),
          message: t('toast.errorPhoneDescription'),
        };
      }
      return {
        title: t('toast.errorGeneric'),
        message: t('toast.errorGenericDescription'),
      };
    },
    [t],
  );

  const validateCurrentStep = useCallback(() => {
    if (!isStepValid) {
      // Set error state for fields with errors (for visual styling)
      errorFields.forEach((field) => {
        setFieldError(field, 'error'); // Just mark as error, no specific message
      });
      return false;
    }
    clearAllErrors();
    return true;
  }, [isStepValid, errorFields, setFieldError, clearAllErrors]);

  const nextStep = useCallback(() => {
    if (validateCurrentStep()) {
      if (currentStep === FORM_STEPS.DETAILS) {
        setCurrentStep(FORM_STEPS.CONTACTS);
      } else if (currentStep === FORM_STEPS.CONTACTS) {
        setCurrentStep(FORM_STEPS.APROVE);
      }
    } else {
      // Show toast message when validation fails
      const { title, message } = getValidationToastMessage(errorFields, formData);
      showToast('error', title, message);
    }
  }, [currentStep, validateCurrentStep, getValidationToastMessage, errorFields, formData]);

  const previousStep = useCallback(() => {
    if (currentStep === FORM_STEPS.APROVE) {
      setCurrentStep(FORM_STEPS.CONTACTS);
    } else if (currentStep === FORM_STEPS.CONTACTS) {
      setCurrentStep(FORM_STEPS.DETAILS);
    }
    clearAllErrors();
  }, [currentStep, clearAllErrors]);

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' });
    setCurrentStep(FORM_STEPS.DETAILS);
    clearAllErrors();
  }, [clearAllErrors]);

  return {
    currentStep,
    formData,
    errors,
    errorFields,
    updateField,
    validateCurrentStep,
    nextStep,
    previousStep,
    resetForm,
    getValidationToastMessage,
    isStepValid,
  };
};
